// Pure reducer over the Architecture document. Every mutation goes through
// here so dirty-tracking and (future) optimistic-undo are trivial.
//
// Versions are NOT created here — they're appended server-side on PATCH save.
// Local mutations just mark the document dirty until the user clicks Save.

import { canNest, descendantIds, isStructuralType } from "./archModel";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

// Best-effort category -> tier mapping for sidebar resources that don't carry
// an explicit tier on the cloud_resources catalog item.
export function categoryToTier(category) {
  const c = (category || "").toLowerCase();
  if (/(database|storage|analytics|warehouse|lake|sql|nosql|data)/.test(c)) return "data";
  if (/(network|cdn|dns|gateway|load|firewall)/.test(c)) return "networking";
  if (/(security|identity|key|secret|threat|iam)/.test(c)) return "security";
  if (/(web|cdn|edge|frontend|ui|ml|ai|media)/.test(c)) return "frontend";
  return "compute";
}

// NOTE: state carried a `history: []` array for a stated "future optimistic-undo"
// that nothing ever read or wrote. Removed — undo today is server-side via the
// versions[] snapshots the Output page restores through RESTORE_SNAPSHOT, so a
// second, local, always-empty stack was only ever misleading.
export function makeInitialState(archDoc) {
  return {
    doc: archDoc,
    dirty: false,
  };
}

// All actions return a new state. They never mutate inputs.
export function archReducer(state, action) {
  switch (action.type) {
    case "LOAD_FROM_SERVER":
      return { doc: action.doc, dirty: false };

    case "MARK_CLEAN":
      return { ...state, dirty: false };

    case "RESTORE_SNAPSHOT": {
      // Load a historical snapshot into the LIVE editor. versions[] is kept
      // intact (history is never destroyed); only nodes + peerConnections
      // change. The dirty flag flips on so the user must Save to commit a
      // new version (server PATCH will append it with label "restored from vN").
      const { nodes, peerConnections } = action;
      if (!Array.isArray(nodes)) return state;
      // Deep-clone the snapshot to prevent later edits from mutating history.
      const cloneNodes = nodes.map((n) => ({ ...n, config: { ...(n.config || {}) } }));
      const clonePeers = (peerConnections || []).map((p) => ({ ...p }));
      return {
        ...state,
        dirty: true,
        doc: {
          ...state.doc,
          nodes: cloneNodes,
          peerConnections: clonePeers,
        },
      };
    }

    case "ADD_RESOURCE": {
      // Add a deployable resource under a parent. Caller is responsible for
      // calling canNest first; reducer enforces it as a safety net.
      const { parentId, name, nodeType, tier, description, reasoning, docsUrl } = action;
      const parent = state.doc.nodes.find((n) => n.id === parentId);
      if (!parent) return state;
      if (!canNest(state.doc.provider, nodeType, parent.type, "resource")) return state;
      const newNode = {
        id: uid(),
        type: nodeType,
        kind: "resource",
        name,
        parentId,
        config: { tier: tier || "compute" },
        description: description || "",
        reasoning: reasoning || "",
        docsUrl: docsUrl || "",
      };
      return {
        ...state,
        dirty: true,
        doc: { ...state.doc, nodes: [...state.doc.nodes, newNode] },
      };
    }

    case "ADD_STRUCTURAL": {
      // Add a structural container (e.g. a new subnet under a VPC, a new
      // resource group under a subscription). Validates via canNest.
      const { parentId, nodeType, name } = action;
      const parent = state.doc.nodes.find((n) => n.id === parentId);
      if (!parent) return state;
      if (!canNest(state.doc.provider, nodeType, parent.type, "structural")) return state;
      const newNode = {
        id: uid(),
        type: nodeType,
        kind: "structural",
        name: name || nodeType,
        parentId,
        config: {},
        description: "",
        reasoning: "",
      };
      return {
        ...state,
        dirty: true,
        doc: { ...state.doc, nodes: [...state.doc.nodes, newNode] },
      };
    }

    case "RENAME_NODE": {
      const { id, name } = action;
      const trimmed = (name || "").trim();
      if (!trimmed) return state;
      return {
        ...state,
        dirty: true,
        doc: {
          ...state.doc,
          nodes: state.doc.nodes.map((n) => (n.id === id ? { ...n, name: trimmed } : n)),
        },
      };
    }

    case "REMOVE_NODE": {
      const { id } = action;
      const node = state.doc.nodes.find((n) => n.id === id);
      if (!node) return state;
      if (!node.parentId) return state; // cannot remove the root
      const dropIds = new Set([id, ...descendantIds(state.doc.nodes, id)]);
      return {
        ...state,
        dirty: true,
        doc: {
          ...state.doc,
          nodes: state.doc.nodes.filter((n) => !dropIds.has(n.id)),
          peerConnections: state.doc.peerConnections.filter(
            (p) => !dropIds.has(p.sourceId) && !dropIds.has(p.targetId)
          ),
        },
      };
    }

    case "MOVE_NODE": {
      // Re-parent a node. Validates canNest; rejects on cycles (cannot move a
      // node under one of its own descendants).
      const { id, newParentId } = action;
      const node = state.doc.nodes.find((n) => n.id === id);
      const parent = state.doc.nodes.find((n) => n.id === newParentId);
      if (!node || !parent) return state;
      if (id === newParentId) return state;
      const descendants = new Set(descendantIds(state.doc.nodes, id));
      if (descendants.has(newParentId)) return state; // cycle
      if (!canNest(state.doc.provider, node.type, parent.type, node.kind)) return state;
      return {
        ...state,
        dirty: true,
        doc: {
          ...state.doc,
          nodes: state.doc.nodes.map((n) => (n.id === id ? { ...n, parentId: newParentId } : n)),
        },
      };
    }

    case "ADD_PEER": {
      const { sourceId, targetId, label } = action;
      if (!sourceId || !targetId || sourceId === targetId) return state;
      const ids = new Set(state.doc.nodes.map((n) => n.id));
      if (!ids.has(sourceId) || !ids.has(targetId)) return state;
      const peer = {
        id: uid(),
        sourceId,
        targetId,
        label: (label || "connects to").trim(),
      };
      return {
        ...state,
        dirty: true,
        doc: {
          ...state.doc,
          peerConnections: [...state.doc.peerConnections, peer],
        },
      };
    }

    case "REMOVE_PEER": {
      const { id } = action;
      return {
        ...state,
        dirty: true,
        doc: {
          ...state.doc,
          peerConnections: state.doc.peerConnections.filter((p) => p.id !== id),
        },
      };
    }

    default:
      return state;
  }
}

/** Helper used by remove confirmation dialogs to summarize cascade scope. */
export function describeCascade(nodes, peerConnections, id) {
  const descIds = descendantIds(nodes, id);
  const removedSet = new Set([id, ...descIds]);
  const peerCount = peerConnections.filter(
    (p) => removedSet.has(p.sourceId) || removedSet.has(p.targetId)
  ).length;
  return { descendantCount: descIds.length, peerCount };
}

export { isStructuralType };
